import { useContext, useEffect, useState } from "react";
import OneSignal from "react-onesignal";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import GlobalFonts from "./app_fonts";
import GlobalStyles from "./app_styles";
import BlipChatComponent from "./components/BlipChat/BlipChat";
import ImportacaoMain from "./components/ProdutosPlanilha/ImportacaoMain/ImportacaoMain";
import TabelaEdicao from "./components/ProdutosPlanilha/TabelaEdicao/TabelaEdicao";
import { AuthContext } from "./contexts/Auth/AuthContext";
import { EmpresaApi } from "./hooks/empresaApi";
import { AssinaturaApi } from "./hooks/assinaturaApi";
import { CatalogoProvider } from "./contexts/Catalogo/CatalogoProvider";
import { ConfiguracoesProvider } from "./contexts/Configuracoes/ConfiguracoesProvider";
import { IntegracaoProvider } from "./contexts/Integracao/IntegracaoProvider";
import { ListasDePrecoProvider } from "./contexts/Listas/ListasDePrecoProvider";
import { PerfilProvider } from "./contexts/Perfil/PerfilProvider";
import { ProdutosProvider } from "./contexts/Produtos/ProdutosProvider";
import { RelatoriosProvider } from "./contexts/Relatorios/RelatoriosProvider";
import { useTheme } from "./contexts/Theme/ThemeContext";
import Catalogo from "./pages/Catalogos/Catalogo/Catalogo";
import Planos from "./pages/Planos/Planos";
import Catalogos from "./pages/Catalogos/Catalogos";
import Cesta from "./pages/CestasAbandonadas/Cesta/Cesta";
import CestasAbandonadas from "./pages/CestasAbandonadas/CestasAbandonadas";
import Compartilhar from "./pages/Compartilhar/Compartilhar";
import Configuracoes from "./pages/Configuracoes/Configuracoes";
import { Metricas } from "./pages/Metricas/Metricas";
import Home from "./pages/Home/Home";
import Login from "./pages/Login/Login";
import Novidades from "./pages/Novidades/Novidades";
import { PagSeguroAutorizado } from "./pages/PagSeguroAutorizado/PagSeguroAutorizado";
import { AtualizarDadosCadastrais } from "./pages/AtualizarDadosCadastrais/AtualizarDadosCadastrais";
import RedefinirSenha from "./pages/RedefinirSenha/RedefinirSenha";
import Pedido from "./pages/Pedidos/Pedido/Pedido";
import Pedidos from "./pages/Pedidos/Pedidos";
import { Perfil } from "./pages/Perfil/Perfil";
import { Assinatura } from "./pages/Assinatura/Assinatura";
import { TrocarPlano } from "./pages/Assinatura/TrocarPlano/TrocarPlano";
import ImportarProdutos from "./pages/Produtos/ImportarProdutos/ImportarProdutos";
import InformacoesERP from "./pages/Produtos/InformacoesERP/InformacoesERP";
import Produto from "./pages/Produtos/Produto/Produto";
import Produtos from "./pages/Produtos/Produtos";
import Relatorios from "./pages/Relatorios/Relatorios";
import { PERMISSOES } from "./services/Consts";
import ModosImportacao from "./pages/Produtos/ModosImportacao.tsx/ModosImportacao";
import { Dashboard } from "./pages/Dashboard/Dashboard";
import Credenciais from "./pages/Credenciais/Credenciais";
import Vitrine from "./pages/ClubeConectado/Vitrine";
import TrilhaCompleta from "./pages/ClubeConectado/TrilhaCompleta/TrilhaCompleta";
import Aula from "./pages/ClubeConectado/Aula/Aula";

function App() {
  const { user, permissoesUsuario, customizadas, loading, permissoesPlanos } =
    useContext(AuthContext);
  const location = useLocation();
  const { theme } = useTheme();

  // Etapa 3 — modo restrito: redireciona pra /assinatura quando empresa
  // do fluxo novo está com ativo=3. Bloqueio é só no front (cliente fica
  // no máximo 10 dias nesse estado; após D+15 vira ativo=0 e nem loga).
  const [modoRestrito, setModoRestrito] = useState(false);

  useEffect(() => {
    if (!user) return;
    AssinaturaApi()
      .getInfoAssinatura()
      .then((info) => {
        setModoRestrito(!!(info.fluxo_novo && info.modo_restrito));
      })
      .catch(() => {
        // Empresa legada ou erro de rede — modo_restrito não se aplica
      });
  }, [user]);

  useEffect(() => {
    const scrollPosition = sessionStorage.getItem("produtos_scroll_position");
    if (location.pathname === "/painel/produtos" && scrollPosition) {
      return;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Ajustar z-index do Planweb stories e Kommo webchat
  useEffect(() => {
    const interval = setInterval(() => {
      // Baixar z-index do Planweb stories (dentro do shadow DOM)
      const widget = document.querySelector(".stories-video-planweb");
      if (widget?.shadowRoot) {
        const fixedDiv = widget.shadowRoot.querySelector(
          'div[style*="z-index: 9999999"]'
        ) as HTMLElement;
        if (fixedDiv) {
          fixedDiv.style.zIndex = "298";
        }
      }

      // Subir z-index do Kommo (inline style sobrescreve)
      const kommo = document.querySelector(".amo-button-holder") as HTMLElement;
      if (kommo) {
        kommo.style.zIndex = "299";
      }

      if (widget?.shadowRoot && kommo) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Kommo webchat: mostrar apenas na home e logado (carrega apenas uma vez)
  useEffect(() => {
    const isLoggedIn = user !== null && user !== false;
    const isHome = location.pathname === "/";
    const styleEl = document.getElementById("kommo-hide-style");
    const kommoJaCarregado = !!document.getElementById("crm_plugin_script");

    if (isLoggedIn && isHome) {
      styleEl?.remove();

      if (!kommoJaCarregado) {
        // Enviar dados do usuário logado para o Kommo webchat (apenas na primeira vez)
        const empresaApi = EmpresaApi();
        empresaApi.get_empresa_usuario().then((empresa) => {
          if (empresa) {
            const metaData = {
              contact: {
                name: empresa.empresa,
                custom_fields_values: [
                  {
                    field_id: 1559682,
                    values: [{ value: empresa.email, enum_code: "WORK" }],
                  },
                ],
              },
              lead: {
                name: empresa.empresa,
              },
            };

            (window as any).crm_plugin.setMeta(metaData);

            // Carregar o script do Kommo uma única vez
            const s = document.createElement("script");
            s.async = true;
            s.id = "crm_plugin_script";
            s.src = "https://gso.kommo.com/js/button.js";
            document.head.appendChild(s);
          }
        }).catch(() => {});
      }
    } else if (!styleEl) {
      const style = document.createElement("style");
      style.id = "kommo-hide-style";
      style.textContent = `
        .amo-button-holder {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, [location.pathname, user]);

  useEffect(() => {
    const isLocal =
      window.location.href.includes("localhost") ||
      window.location.href.includes("dev");

    try {
      OneSignal.init({
        appId: isLocal
          ? "d784bf9a-1656-4987-a339-bcbb23b83e54"
          : "b5c71943-0f1c-45bd-8482-5c80451710d6",
        allowLocalhostAsSecureOrigin: isLocal,
        welcomeNotification: {
          disable: true,
        },
        promptOptions: {
          slidedown: {
            prompts: [
              {
                type: "push",
                autoPrompt: false,
                text: {
                  actionMessage: "Ativar notificações neste navegador?",
                  acceptButton: "Ativar",
                  cancelButton: "Não",
                },
              },
            ],
          },
        },
      });
    } catch (error) {
      console.log("Erro ao inicializar OneSignal", error);
    }
  }, []);

  // Rota de assinatura: vê quem tem 180 OU é Gerente não-customizado (tem 7, sem pílula 150-153). Não exige 7 junto do 180.
  const temPilulaUsuarios = [
    PERMISSOES.USUARIOS_CRIAR,
    PERMISSOES.USUARIOS_EDITAR_GERAL,
    PERMISSOES.USUARIOS_EDITAR_MEU,
    PERMISSOES.USUARIOS_EXCLUIR,
  ].some((id) => permissoesUsuario.includes(id));
  const podeVerAssinatura =
    permissoesUsuario.includes(PERMISSOES.ASSINATURAS_VER) ||
    (permissoesUsuario.includes(PERMISSOES.USUARIOS_GERENTE) && !temPilulaUsuarios);
  // Métricas não tem permissão grossa; customizado só vê com 160/161, não-customizado vê como hoje.
  const podeVerMetricas =
    !customizadas ||
    permissoesUsuario.includes(PERMISSOES.METRICAS_GERAL) ||
    permissoesUsuario.includes(PERMISSOES.METRICAS_MINHAS);

  return (
    <>
      <GlobalStyles theme={theme} />
      <GlobalFonts />
      <div className="App" id="App">
        {user !== null && user !== false && (
          modoRestrito && !location.pathname.startsWith("/assinatura") ? (
            <Navigate to="/assinatura" replace />
          ) : (
          <>
            <Routes>
              <Route path="/" element={<Dashboard />}>
                <Route
                  index
                  element={
                    <>
                      <Home permissoesPlanos={permissoesPlanos} />
                      {/* <BlipChatComponent /> */}
                    </>
                  }
                ></Route>
                <Route path="/compartilhar" element={<Compartilhar />}></Route>
                <Route path="painel/credenciais/:token" element={<Credenciais />}></Route>
                {(podeVerMetricas || loading) && (
                  <Route path="/metricas" element={<Metricas />}></Route>
                )}
                <Route path="/novidades" element={<Novidades permissoesPlanos={permissoesPlanos}/>}></Route>
                 <Route path="/destaques" element={<Novidades permissoesPlanos={permissoesPlanos} />}></Route>
                <Route path="/clube-conectado" element={<Vitrine />}></Route>
                <Route path="/clube-conectado/trilha/:id/:name?" element={<TrilhaCompleta />}></Route>
                <Route path="/clube-conectado/trilha/:trilhaId/:trilhaNome/aula/:id/:name" element={<Aula />}></Route>
                <Route path="/clube-conectado/aula/:id/:name?" element={<Aula />}></Route>
                <Route path="/painel/pedidos" element={<Pedidos />}></Route>
                <Route path="/perfil/usuarios/planos" element={<Planos />}></Route>
                <Route
                  path="/painel/pedidos/pedido/:id"
                  element={<Pedido />}
                ></Route>
                <Route
                  path="/painel/sacolas-abandonadas"
                  element={<CestasAbandonadas permissoesPlanos={permissoesPlanos}/>}
                ></Route>
                <Route
                  path="/painel/sacolas-abandonadas/sacola-abandonada/:id/:name"
                  element={<Cesta />}
                ></Route>
                {(permissoesUsuario.includes(PERMISSOES.USUARIOS_GERENTE) ||
                  loading) && (
                  <Route
                    path="/perfil/usuarios"
                    element={
                      <PerfilProvider>
                        <Perfil />
                      </PerfilProvider>
                    }
                  ></Route>
                )}
                {(modoRestrito || podeVerAssinatura || loading) && (
                  <Route
                    path="/assinatura"
                    element={<Assinatura />}
                  ></Route>
                )}
                {(modoRestrito || podeVerAssinatura || loading) && (
                  <Route
                    path="/assinatura/trocar-plano"
                    element={<TrocarPlano />}
                  ></Route>
                )}
                {(permissoesUsuario.includes(PERMISSOES.CONFIGURACOES) ||
                  loading) && (
                  <Route
                    path="/painel/configuracoes/:anchor?"
                    element={
                      <ConfiguracoesProvider>
                        <IntegracaoProvider>
                          <Configuracoes />
                        </IntegracaoProvider>
                      </ConfiguracoesProvider>
                    }
                  ></Route>
                )}
                {(permissoesUsuario.includes(PERMISSOES.CATALOGOS) ||
                  loading) && (
                  <>
                    <Route
                      path="/painel/catalogos"
                      element={<Catalogos />}
                    ></Route>
                    <Route
                      path="/painel/catalogos/catalogo/:id/:name/:duplicated?"
                      element={
                        <ConfiguracoesProvider>
                          <CatalogoProvider>
                            <Catalogo permissoesPlanos={permissoesPlanos} />
                          </CatalogoProvider>
                        </ConfiguracoesProvider>
                      }
                    ></Route>
                  </>
                )}
                {(permissoesUsuario.includes(PERMISSOES.PRODUTOS) ||
                  loading) && (
                  <>
                    <Route
                      path="/painel/produtos/produto/:id/:duplicated?"
                      element={
                        <ListasDePrecoProvider>
                          <ConfiguracoesProvider>
                            <Produto permissoesPlano={permissoesPlanos} />
                          </ConfiguracoesProvider>
                        </ListasDePrecoProvider>
                      }
                    />
                    <Route
                      path="/painel/produtos/importar-imagens/:id?"
                      element={<ModosImportacao permissoesPlanos={permissoesPlanos}/>}
                    />
                    <Route
                      path="/painel/produtos/importar-produtos"
                      element={<ImportarProdutos permissoesPlanos={permissoesPlanos}/>}
                    >
                      <Route index element={<ImportacaoMain />} />
                      <Route path="ajustar" element={<TabelaEdicao />} />
                    </Route>
                    <Route
                      path="/painel/produtos"
                      element={
                        <ProdutosProvider tipoFiltro="listagem-padrao">
                          <ConfiguracoesProvider>
                            <Produtos />
                          </ConfiguracoesProvider>
                        </ProdutosProvider>
                      }
                    ></Route>
                    <Route
                      path="/painel/produtos/informacoes-erp"
                      element={<InformacoesERP />}
                    ></Route>
                    <Route
                      path="/painel/produtos/produto/:id"
                      element={
                        <ListasDePrecoProvider>
                          <ConfiguracoesProvider>
                            <Produto permissoesPlano={permissoesPlanos} />
                          </ConfiguracoesProvider>
                        </ListasDePrecoProvider>
                      }
                    ></Route>
                  </>
                )}
                {(permissoesUsuario.includes(PERMISSOES.RELATORIOS) ||
                  loading) && (
                  <Route
                    path="/painel/relatorios/:id?"
                    element={
                      <RelatoriosProvider>
                        <Relatorios />
                      </RelatoriosProvider>
                    }
                  ></Route>
                )}
                <Route path="*" element={<Navigate to="/" />}></Route>{" "}
                {/* navegar pra home quando a rota nao for encontrada */}
              </Route>
              {/* -------------------------------------------------------------------------- */}
              <Route
                path="/pagseguro-autorizado"
                element={<PagSeguroAutorizado />}
              ></Route>
              <Route
                path="/perfil/atualizar-dados"
                element={<AtualizarDadosCadastrais />}
              ></Route>
              <Route
                path="/redefinir-senha/:hash"
                element={<RedefinirSenha />}
              ></Route>
            </Routes>
          </>
          )
        )}
        <>
          {user === false && (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/perfil/atualizar-dados"
                element={<AtualizarDadosCadastrais />}
              ></Route>
              <Route
                path="/redefinir-senha/:hash"
                element={<RedefinirSenha />}
              ></Route>
              <Route path="*" element={<Navigate to="/login" />}></Route>
            </Routes>
          )}
        </>
      </div>
    </>
  );
}

export default App;
